import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, addMonths, subMonths, startOfWeek, endOfWeek, isToday, parseISO, isValid } from 'date-fns';
import { CalendarPost } from './CalendarPost';
import type { Post } from '../../types/calendar';

interface CalendarViewProps {
  currentDate: Date;
  onDateChange: (date: Date) => void;
  posts: Post[];
  onPostClick?: (post: Post) => void;
  onDrop?: (date: Date) => void;
  dragOverDate?: Date | null;
  onDragOver?: (date: Date | null) => void;
  onPostDragStart?: (e: React.DragEvent, post: Post) => void;
}

export const CalendarView: React.FC<CalendarViewProps> = ({
  currentDate,
  onDateChange,
  posts,
  onPostClick,
  onDrop,
  dragOverDate,
  onDragOver,
  onPostDragStart
}) => {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(monthStart);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Start on Monday
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Compute available height for the grid to fill viewport
  const gridContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [gridHeight, setGridHeight] = React.useState<number | null>(null);

  React.useEffect(() => {
    const measure = () => {
      if (!gridContainerRef.current) return;
      const rect = gridContainerRef.current.getBoundingClientRect();
      const available = window.innerHeight - rect.top - 16; // 16px bottom margin
      setGridHeight(Math.max(available, 300));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const getEffectiveDate = (post: Post): Date | null => {
    // Ideas use reminderAt as their calendar date
    if (post.status === 'idea') {
      const candidate = post.reminderAt || null;
      if (!candidate) return null;
      let d = parseISO(candidate);
      if (!isValid(d)) d = new Date(candidate);
      return isValid(d) ? d : null;
    }
    // Prefer publishedAt, fallback to scheduledAt, else ignore
    const candidate = post.publishedAt || post.scheduledAt || null;
    if (!candidate) return null;
    let d = parseISO(candidate);
    if (!isValid(d)) d = new Date(candidate);
    return isValid(d) ? d : null;
  };

  const getPostsForDate = (date: Date) => {
    const targetDateStr = format(date, 'yyyy-MM-dd');
    
    return posts.filter(post => {
      try {
        const effective = getEffectiveDate(post);
        if (!effective) return false;
        const dateStr = format(effective, 'yyyy-MM-dd');
        return dateStr === targetDateStr;
      } catch (error) {
        console.error(`Error processing post ${post.id}:`, error);
        return false;
      }
    });
  };

  const handlePreviousMonth = () => {
    onDateChange(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    onDateChange(addMonths(currentDate, 1));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault(); // Allow drop
  };

  const handleDayDragOver = (e: React.DragEvent, date: Date) => {
    e.preventDefault();
    if (onDragOver) {
      onDragOver(date);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    // Only clear dragOver if we're leaving the day cell entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      if (onDragOver) {
        onDragOver(null);
      }
    }
  };

  const handleDayDrop = (e: React.DragEvent, date: Date) => {
    e.preventDefault();
    if (onDragOver) {
      onDragOver(null);
    }
    if (onDrop) {
      onDrop(date);
    }
  };

  // Always show the calendar grid, even when empty

  return (
    <div className="bg-white rounded-xl border border-black shadow-lg overflow-hidden w-full max-w-full">
      <div className="overflow-x-auto">
        <div className="p-2 sm:p-4 flex items-center justify-between border-b border-gray-200">
          <button
            onClick={handlePreviousMonth}
            className="p-1 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          </button>
          <h2 className="text-lg sm:text-xl font-semibold">
            {format(currentDate, 'MMMM yyyy')}
          </h2>
          <button
            onClick={handleNextMonth}
            className="p-1 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          </button>
        </div>

        <div className="grid grid-cols-7 border-b border-gray-200 min-w-[320px]">
          {weekDays.map(day => (
            <div
              key={day}
              className="py-1 sm:py-2 text-center text-xs sm:text-sm font-medium text-gray-700"
            >
              {day}
            </div>
          ))}
        </div>

        <div
          ref={gridContainerRef}
          className="grid grid-cols-7 auto-rows-fr divide-x divide-y w-full min-w-[320px]"
          style={gridHeight ? { height: gridHeight } : undefined}
        >
          {days.map((day, dayIdx) => {
            const dayPosts = getPostsForDate(day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            const dayIsToday = isToday(day);
            const isDragOver = dragOverDate && format(dragOverDate, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd');

            return (
              <div
                key={day.toString()}
                className={`p-1 sm:p-2 overflow-hidden transition-colors h-full flex flex-col ${
                  !isCurrentMonth 
                    ? 'bg-gray-50' 
                    : isDragOver 
                      ? 'bg-primary-50' 
                      : 'bg-white'
                } hover:bg-gray-50`}
                onDragOver={(e) => handleDayDragOver(e, day)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDayDrop(e, day)}
              >
                <div className="flex justify-start">
                  <div className={`flex items-center justify-center w-5 h-5 sm:w-7 sm:h-7 rounded-full ${
                    dayIsToday ? 'bg-red-500' : ''
                  }`}>
                    <p className={`text-xs sm:text-sm font-medium ${
                      !isCurrentMonth 
                        ? 'text-gray-400' 
                        : dayIsToday 
                          ? 'text-white' 
                          : 'text-gray-900'
                    }`}>
                      {format(day, 'd')}
                    </p>
                  </div>
                </div>
                <div className="space-y-0.5 sm:space-y-1 mt-0.5 sm:mt-1 overflow-y-auto flex-1">
                  {dayPosts.map(post => (
                    <div 
                      key={post.id} 
                      onClick={() => onPostClick?.(post)}
                      className="cursor-pointer"
                    >
                      <CalendarPost 
                        post={post} 
                        onDragStart={onPostDragStart}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};